import React from 'react';
import PnlReportGrid from 'src/components/star-grid/components/PnlReportGrid';
import type { QueryResponse } from 'src/components/star-grid/api/reportEngineTypes';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { PNL_STATEMENT_LINE_DIMENSION } from 'src/components/star-grid/starGridConstants';
import { REPORT_ROW_ID } from 'src/components/star-grid/reportDefinitionConstants';
import { PNL_DEFAULT_SCALE } from 'src/components/star-grid/utils/pnlGrid';
import { buildReportPivotModel } from 'src/components/star-grid/utils/reportPivotModel';

export interface ReportResultGridProps {
  /** Per-row drill wiring. Absent in the studio preview, where there is no saved report to drill. */
  readonly rowDrillTargets?: readonly { dimension: string; hierarchy: string; label: string }[];
  readonly onRowDrill?: (memberId: string, indent: number, target: { dimension: string; hierarchy: string; label: string }) => void;
  readonly onRowDrillMenu?: () => void;
  readonly isRowExpanded?: (memberId: string) => boolean;
  readonly onRowCollapse?: (memberId: string) => void;
  readonly onFilterByRow?: (memberId: string, label: string) => void;
  /** Opens or closes an expandable drilled row's next level, by row key. */
  readonly onRowToggle?: (rowKey: string, indent: number) => void;
  /** On a two-dimension report: opens or closes a row's OUTER line, by its outer member. */
  readonly onOuterToggle?: (outerMember: string, indent: number) => void;
  readonly isOuterExpanded?: (outerMember: string) => boolean;
  readonly response: QueryResponse | undefined;
  readonly text: StarGridText;
  /** True while a (re-)query is in flight — shows the grid's loading overlay. */
  readonly loading?: boolean;
  /** Selected display-scale id. Values arrive unscaled; the grid applies the divide. */
  readonly scaleId?: string;
  readonly compactColumnHeaders?: boolean;
  /** Heads the pinned row column, the way the deck names its line-item column. */
  readonly reportName?: string;
  /** Grow to fit every row instead of scrolling in a fixed viewport — see `PnlReportGridProps`. */
  readonly autoHeight?: boolean;
}

/** Engine rows are the whole statement; there is no dimension left to drill into. */
const NO_DRILL_DIMENSIONS: readonly string[] = [];
const ROW_DIMENSIONS: readonly string[] = [PNL_STATEMENT_LINE_DIMENSION];
const noDrill = (): void => undefined;

/**
 * Renders an engine `QueryResponse` through `PnlReportGrid`, so the studio preview and the report
 * viewer share one translation of the response.
 */
export const ReportResultGrid: React.FC<ReportResultGridProps> = (props) => {
  const model = React.useMemo(() => buildReportPivotModel(props.response), [props.response]);
  const dimensionLabels = React.useMemo(
    () => ({ [PNL_STATEMENT_LINE_DIMENSION]: props.reportName?.trim() || props.text('rowLabelColumn') }),
    [props.reportName, props.text]
  );
  return (
    <PnlReportGrid
      model={model}
      loading={props.loading ?? false}
      scaleId={props.scaleId ?? PNL_DEFAULT_SCALE.id}
      reportName={props.reportName}
      compactColumnHeaders={props.compactColumnHeaders}
      autoHeight={props.autoHeight}
      dimensionLabels={dimensionLabels}
      groupBy={ROW_DIMENSIONS}
      // Whole-report re-grouping stays off: the row axis is the statement, so it has no
      // meaning. The per-ROW drill below is the deck's own behaviour and is what these props enable.
      drillableDimensions={NO_DRILL_DIMENSIONS}
      onDrillDown={noDrill}
      rowDrillTargets={props.rowDrillTargets}
      onRowDrillMenu={props.onRowDrillMenu}
      onRowDrill={
        props.onRowDrill ? (row, target) => props.onRowDrill?.(String(row[REPORT_ROW_ID] ?? ''), Number(row.axis_indent ?? 0), target) : undefined
      }
      isRowExpanded={props.isRowExpanded ? (row) => props.isRowExpanded?.(String(row[REPORT_ROW_ID] ?? '')) ?? false : undefined}
      onRowCollapse={props.onRowCollapse ? (row) => props.onRowCollapse?.(String(row[REPORT_ROW_ID] ?? '')) : undefined}
      onFilterByRow={
        // Filter by scopes to the MEMBER, not the drill key: it is a report filter, not a row address.
        props.onFilterByRow ? (row) => props.onFilterByRow?.(String(row[PNL_STATEMENT_LINE_DIMENSION] ?? ''), String(row.label ?? '')) : undefined
      }
      // Clicking an expandable label opens (or closes) its next level, as in the deck. On a two-dimension
      // row the first label is the outer line and the second the inner member, each expanding on its own.
      onRowLabelClick={
        props.onRowToggle
          ? (row, field) => {
              const outer = row.axis_outer_member === undefined ? undefined : String(row.axis_outer_member);
              if (outer !== undefined && field === PNL_STATEMENT_LINE_DIMENSION) {
                if (row.axis_outer_expandable === true || props.isOuterExpanded?.(outer)) props.onOuterToggle?.(outer, Number(row.axis_indent ?? 0));
                return;
              }
              if (row.axis_expandable === true || props.isRowExpanded?.(String(row[REPORT_ROW_ID] ?? '')))
                props.onRowToggle?.(String(row[REPORT_ROW_ID] ?? ''), Number(row.axis_indent ?? 0));
            }
          : undefined
      }
      isOuterExpanded={
        props.isOuterExpanded
          ? (row) => row.axis_outer_member !== undefined && (props.isOuterExpanded?.(String(row.axis_outer_member)) ?? false)
          : undefined
      }
    />
  );
};
